import { IRange } from "../comments/CommentContainer";

const characterPattern = /^CHARACTER\((?<character>[^),]+)(?:,\s*(?<parenthetical>[^)]+))?\):\s*(?<line>.*)/;
const commentPattern = /^---$/;
const conditionalPattern = /^CONDITIONAL\((?<conditional>[^,]+),\s*(?<condition>.+)\)/;
const cutPattern = /^CUT:\s*(?<cut>.*)$/;
const linePattern = /^.*$/;
const notePattern = /^NOTE(?:\((?<note>[^)]*)\))?:\s*(?<line>.*)/;
const stillPattern = /^STILL\((?<still>[^)]+)\)/;
const titlePattern = /^TITLE\((?<title>[^)]+)\)/;
const gesturePattern = /\*\s*Gesture choice: (?<gesture>.*)/;
const newGesturePattern = /^GESTURE\((?<gesture>[^)]+)\)/;
const optionPattern = /^\s*\*\s*(?<option>.*)/;
const newOptionPattern = /^OPTION\((?<option>[^)]+)\)/;

export type ScriptTypes = "character" | "comment" | "cut" | "gesture" | "paragraph" | "note" | "conditional" |
    "option" | "still" | "title"

export interface IScriptElement
{
    type: ScriptTypes
    data: unknown
    mappedRange: IRange | Array<IRange>
}
export interface IParagraphData
{
    offset: number
    lines: string[]
}
export interface ICharacterData extends IParagraphData
{
    character: string
    parenthetical: string
}
export interface INoteData
{
    note: string
    line: string
}
export type ScriptElementData = {[key: string]: string};
export function parseScript(script: string): IScriptElement[]
{
    const parsed: IScriptElement[] = [];
    const lines = script.split('\n');
    const tryMatch = (pattern: RegExp, test: string, callback: (result: RegExpMatchArray) => void): boolean => {
        const match = test.match(pattern);
        
        if (match) {
            callback(match);
            return true;
        }
        return false;
    };
    const extendLastLine = (type: ScriptTypes, mappedRange: IRange, keys: string[] = []) => (match: RegExpMatchArray) => {
        const offset = match[0].indexOf(match[1]);
        const range =  {start: mappedRange.start + offset, end: mappedRange.end + offset};
        const data: ScriptElementData = {text: match.groups![type]};

        keys.forEach(key => data[key] = match.groups![key]);
        if (lastLine.type === type) {
            (lastLine.data as ScriptElementData[]).push(data);
            (lastLine.mappedRange as IRange[]).push(range);
        } else {
            lastLine = {type, data: [data], mappedRange:[range]};
            parsed.push(lastLine);
        }
    };
    const simpleLine = (type: ScriptTypes, mappedRange: IRange) => (match: RegExpMatchArray) => {
        const offset = match[0].indexOf(match[1]);
        const range = {start:mappedRange.start + offset, end: mappedRange.end + offset};
        const data: ScriptElementData = { text: match.groups ? match.groups[type] : "" };

        lastLine = {type, data, mappedRange: range};
        parsed.push(lastLine);
    };
    let lastLine: IScriptElement = {type: "firstLine" as ScriptTypes, data: {}, mappedRange: {start: 0, end: 0}};

    let start = -1;
    let end = -1;
    lines.forEach(line => {
        start = end + 1;
        end = start + line.length;
        const mappedRange: IRange = {start, end}
        tryMatch(stillPattern, line, simpleLine("still", mappedRange)) ||
        tryMatch(gesturePattern, line, extendLastLine("gesture", mappedRange)) ||
        tryMatch(newGesturePattern, line, extendLastLine("gesture", mappedRange)) ||
        tryMatch(conditionalPattern, line, extendLastLine("conditional", mappedRange, ["condition"])) ||
        tryMatch(optionPattern, line, extendLastLine("option", mappedRange)) ||
        tryMatch(newOptionPattern, line, extendLastLine("option", mappedRange)) ||
        tryMatch(characterPattern, line, match => {
            const offset = line.indexOf(match.groups?.line ?? "") || match[0].length;
            const range = {
                start: mappedRange.start + offset,
                end: mappedRange.end + offset
            };

            lastLine = {
                type: "character",
                data: {
                    character: match.groups?.character, 
                    parenthetical: match.groups?.parenthetical,
                    lines: [match.groups?.line],
                    offset
                },
                mappedRange: [range]
            };
            parsed.push(lastLine);
        }) ||
        tryMatch(titlePattern, line, simpleLine("title", mappedRange)) ||
        tryMatch(notePattern, line, match => {
            lastLine = {
                type: "note",
                data: {
                    note: match.groups?.note || "NOTE",
                    line: match.groups!.line
                },
                mappedRange
            };
            parsed.push(lastLine);
        }) ||
        tryMatch(cutPattern, line, simpleLine("cut", mappedRange)) ||
        tryMatch(commentPattern, line, simpleLine("comment", mappedRange)) ||
        tryMatch(linePattern, line, match => {
            if (lastLine.type === "character" || lastLine.type === "paragraph") {
                (lastLine.data as IParagraphData).lines.push(line);
                (lastLine.mappedRange as IRange[]).push(mappedRange);
            } else {
                lastLine = {type: "paragraph", data: {offset: 0, lines: [line]}, mappedRange: [mappedRange]};
                parsed.push(lastLine);
            }
        });
    });
    return parsed;
}